import { type Logger, TaggedLogger } from '../logging/logger.ts'
import { Result } from '../models/result.ts'
import {
    AbstractDataStore,
    type DataStoreGetResult,
    DataStoreName,
    type DataStoreRecord,
    StorageType,
} from './abstract-data-store.ts'
import { Env } from '../env.ts'
import { Globals } from '../globals.ts'
import { PNException } from '../exceptions/pn-exception.ts'
import { PNExceptionCode } from '../exceptions/pn-exception-code.ts'
import { ensureError } from '../utils/ensure-error.ts'

const IDB_DB_NAME = 'pn_store'
const IDB_DB_VERSION = 1

type TxCallback<T> = (result: Result<T>) => void

/** @internal */
export const IdbDataStoreException = {
    notAvailable: () => new PNException(PNExceptionCode.STORAGE_NOT_AVAILABLE, null, ' - IndexedDB'),
    noDataFor: (key: string) => new PNException(PNExceptionCode.STORAGE_KEY_EMPTY, null, key),
    unableToOpen: (reason: Error) => new PNException(PNExceptionCode.STORAGE_FAILED_TO_OPEN, reason, ` - IndexedDB`),
    invalidStoreName: () => new PNException(PNExceptionCode.IDB_INVALID_STORE_NAME),
    blocked: () => new PNException(PNExceptionCode.IDB_BLOCKED),
} as const

/** @internal */
export class IdbDataStore extends AbstractDataStore {
    public constructor(storeName: DataStoreName | null, logger: Logger = TaggedLogger._getInstance('IdbStore')) {
        super(StorageType.IDB, storeName, logger)
    }

    public get _isAvailable(): boolean {
        return Env._getIsEnvBaseSupported()[0] && !!this._storeName && !!this._indexedDB
    }

    protected get _indexedDB(): IDBFactory {
        return Env._scope.indexedDB
    }

    protected get _isSafari(): boolean {
        return Env._isSafari
    }

    public async _getData<T>(key: string): Promise<DataStoreGetResult<T>> {
        return this._executeTransaction('read', key)
    }

    public async _setData<T>(key: string, value: T): Promise<Result<T>> {
        return this._executeTransaction<T>('write', [key, value], () => {
            if (key !== Globals._pnStorageTypeLuaKey) {
                this._updateLua()
            }
        })
    }

    public async _removeData(key: string): Promise<Result<null>> {
        return this._executeTransaction('delete', key)
    }

    protected async _openDatabase() {
        return new Promise<Result<IDBDatabase>>((res) => {
            if (!this._indexedDB) {
                res(Result.Err(IdbDataStoreException.notAvailable()))
                return
            }

            if (!this._storeName) {
                res(Result.Err(IdbDataStoreException.invalidStoreName()))
                return
            }

            const openReq = this._indexedDB.open(IDB_DB_NAME, IDB_DB_VERSION)

            openReq.onerror = (ev) => {
                res(Result.Err(openReq.error || new Error(ev as any)))
            }

            openReq.onsuccess = (ev) => {
                this._handleOnOpenSuccess((ev.target as IDBOpenDBRequest).result, (db) => {
                    res(Result.Ok(db))
                })
            }

            openReq.onupgradeneeded = (ev) => {
                this._handleOnUpgradeNeeded((ev.target as IDBOpenDBRequest).result)
            }

            openReq.onblocked = () => {
                res(Result.Err(IdbDataStoreException.blocked()))
            }
        })
    }

    protected _handleOnOpenSuccess(db: IDBDatabase, onComplete: (db: IDBDatabase) => void): void {
        db.onversionchange = () => {
            this._handleOnVersionChange(db)
        }
        if (this._isSafari) {
            this._selfHeal(db)
        }
        onComplete(db)
    }

    protected _handleOnUpgradeNeeded(db: IDBDatabase): void {
        if (!db.objectStoreNames.contains(DataStoreName.sub)) {
            db.createObjectStore(DataStoreName.sub, { keyPath: 'k' })
        }
        if (!db.objectStoreNames.contains(DataStoreName.sdk)) {
            db.createObjectStore(DataStoreName.sdk, { keyPath: 'k' })
        }
        if (!db.objectStoreNames.contains(DataStoreName.WORKER)) {
            db.createObjectStore(DataStoreName.WORKER, { keyPath: 'k' })
        }
    }

    protected _handleOnVersionChange(db: IDBDatabase): void {
        db.close()
    }

    protected async _executeTransaction<T>(
        action: 'read',
        key: string,
        onComplete?: TxCallback<T>,
    ): Promise<DataStoreGetResult<T>>
    protected async _executeTransaction<T>(
        action: 'write',
        keyValue: [key: string, value: any],
        onComplete?: TxCallback<T>,
    ): Promise<Result<T>>
    protected async _executeTransaction<T>(
        action: 'delete',
        key: string,
        onComplete?: TxCallback<T>,
    ): Promise<Result<null>>
    protected async _executeTransaction<T>(
        action: 'read' | 'write' | 'delete',
        keyValue: string | [key: string, value: T],
        onComplete?: TxCallback<T>,
    ): Promise<Result<T> | DataStoreGetResult<T>> {
        const req = await this._openDatabase()
        if (req.isErr()) {
            return req
        }

        const db = req.unwrap()

        return new Promise((res) => {
            // Each call opens its own connection; close it once the transaction settles so
            // connections do not accumulate across worker wakes / the page session lifetime.
            // Guarded against a double-close and against `transaction()` throwing synchronously
            // (e.g. a missing object store), which would otherwise leak the connection and
            // leave the promise unresolved.
            let closed = false
            const closeDb = () => {
                if (closed) {
                    return
                }
                closed = true
                try {
                    db.close()
                } catch {
                    /* already closing/closed */
                }
            }

            try {
                const storeName = this._storeName!
                const mode: IDBTransactionMode = action === 'read' ? 'readonly' : 'readwrite'

                const tx = db.transaction(storeName, mode)

                const updatedAt = new Date().getTime()
                const store = tx.objectStore(storeName)

                const key = action === 'write' ? keyValue[0] : (keyValue as string)

                let req: IDBRequest
                switch (action) {
                    case 'read':
                        req = store.get(key)
                        break
                    case 'write': {
                        req = store.put({
                            k: key,
                            value: keyValue[1],
                            ts: key === Globals._pnStorageTypeLuaKey ? keyValue[1] : updatedAt,
                        })
                        break
                    }
                    case 'delete':
                        req = store.delete(key)
                        break
                }

                tx.onabort = (ev) => {
                    closeDb()
                    res(Result.Err(req.error || new Error(ev as any)))
                }

                tx.oncomplete = () => {
                    closeDb()

                    let result: Result<any>
                    switch (action) {
                        case 'read':
                            result = Result.Ok<DataStoreRecord<T>>({
                                value: req.result?.value ?? null,
                                ts: req.result?.ts ?? 0,
                                sourceType: this._storageType,
                            })
                            break
                        case 'write':
                            result = Result.Ok(keyValue[1])
                            break
                        case 'delete':
                            result = Result.Ok(null)
                            break
                    }

                    res(result)
                    onComplete?.(result)
                }
            } catch (err) {
                closeDb()
                res(Result.Err(ensureError(err)))
            }
        })
    }

    protected _updateLua() {
        this._executeTransaction('write', [Globals._pnStorageTypeLuaKey, new Date().getTime()]).then((luaRes) => {
            if (luaRes.isErr()) {
                this._logger.verbose(`Unable to set lua for ${this._storeName}`, luaRes.getErr()?.message)
            }
        })
    }

    protected _selfHeal(db: IDBDatabase): void {
        // Compare against the object-store *names* (enum values: 'sub'/'sdk'/'w'), not the enum
        // keys ('sub'/'sdk'/'WORKER'). Using keys made the `WORKER` check never match the
        // store actually created as 'w', so the guard always fell through and ran the heal on
        // every open even on a healthy DB.
        const dataStores = Object.values(DataStoreName)

        // Work around Safari issue where 'onupgradeneeded' isn't
        // triggered but the object store is missing.
        if (dataStores.every((dataStore) => db.objectStoreNames.contains(dataStore))) {
            return
        }

        // A genuinely missing object store cannot be created outside a versionchange
        // transaction, so this can throw `InvalidStateError`. Guard it: a throw here would
        // escape the open `onsuccess` handler and leave the open promise unresolved.
        try {
            this._handleOnUpgradeNeeded(db)
        } catch (err) {
            this._logger.warn('Self-heal could not create a missing object store outside an upgrade.', ensureError(err))
        }
    }
}
